import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Scale, RefreshCw, Search, AlertTriangle } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { useAuth } from '../context/AuthContext';

const API_BASE = 'https://api.glowytics.com';

// A seller whose catalog is older than this is left out of the rival comparison by default —
// its "match" rows get re-dated every run, but the prices behind them do not.
const STALE_DAYS = 14;
// Within ±1% counts as the same price (same band the backend uses for Google).
const BAND = 1;

const fmt = (v) =>
  v == null ? '—' : `₺${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');
const fmtPct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`);
const side = (d) => (d == null ? null : d > BAND ? 'pricier' : d < -BAND ? 'cheaper' : 'equal');
const daysSince = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null);

const SIDES = ['cheaper', 'equal', 'pricier'];
const SIDE_LABEL = { cheaper: 'We are cheaper', equal: 'Same (±1%)', pricier: 'We are pricier' };
const SIDE_CLS = { cheaper: 'text-emerald-600', equal: 'text-gray-500', pricier: 'text-red-500' };

const VERDICT = {
  agree: { label: 'Agree', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  partial: { label: 'Partly', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  conflict: { label: 'Conflict', cls: 'bg-red-50 text-red-700 border-red-200' },
};
const verdictOf = (a, b) => {
  if (!a || !b) return null;
  if (a === b) return 'agree';
  if (a !== 'equal' && b !== 'equal') return 'conflict';
  return 'partial';
};

const SOURCE_TABS = [
  { k: 'both', label: 'In both' },
  { k: 'rivals', label: 'Only our rival scan' },
  { k: 'google', label: 'Only Google benchmark' },
  { k: '', label: 'All' },
];

export default function PriceBenchmarkVsComparison() {
  const { selectedBrand } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('both');
  const [cell, setCell] = useState(null);            // { r: rivalSide, g: googleSide } from the matrix
  const [search, setSearch] = useState('');
  const [includeStale, setIncludeStale] = useState(false);
  const [sort, setSort] = useState('disagreement');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/competitor-intel/benchmark-vs-comparison`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand?.id || localStorage.getItem('currentBrandId') || '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message);
      toast.error('Could not load comparison. ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const OUR_SITE = data?.ourSite || 'Us';
  const staleSites = useMemo(
    () => (data?.compSites || []).filter((s) => (daysSince(data?.siteDates?.[s]) ?? 999) > STALE_DAYS),
    [data]
  );
  const activeSites = useMemo(
    () => (data?.compSites || []).filter((s) => includeStale || !staleSites.includes(s)),
    [data, includeStale, staleSites]
  );

  // Rival side is recomputed here (not taken from the API) so the stale-seller toggle
  // changes the market minimum, the source bucket and the verdict consistently.
  const allRows = useMemo(() => (data?.rows || []).map((r) => {
    const prices = activeSites.map((s) => r.comps?.[s]?.price).filter((v) => v != null);
    const marketMin = prices.length ? Math.min(...prices) : null;
    const rivalDelta = r.scrapePrice != null && marketMin ? ((r.scrapePrice - marketMin) / marketMin) * 100 : null;
    const rivalSide = side(rivalDelta);
    const hasRivals = prices.length > 0;
    const hasGoogle = r.benchmark != null;
    return {
      ...r,
      ourPrice: r.mcPrice ?? r.scrapePrice,
      marketMin,
      rivalCount: prices.length,
      rivalDelta,
      rivalSide,
      source: hasRivals && hasGoogle ? 'both' : hasRivals ? 'rivals' : hasGoogle ? 'google' : null,
      verdict: verdictOf(rivalSide, r.googleSide),
    };
  }).filter((r) => r.source), [data, activeSites]);

  const both = useMemo(() => allRows.filter((r) => r.source === 'both'), [allRows]);

  const summary = useMemo(() => {
    const c = { both: 0, rivals: 0, google: 0 };
    for (const r of allRows) c[r.source] += 1;
    const matrix = {};
    for (const a of SIDES) for (const b of SIDES) matrix[`${a}|${b}`] = 0;
    let agree = 0; let conflict = 0; let rivalSum = 0; let googleSum = 0;
    for (const r of both) {
      matrix[`${r.rivalSide}|${r.googleSide}`] += 1;
      if (r.verdict === 'agree') agree += 1;
      if (r.verdict === 'conflict') conflict += 1;
      rivalSum += r.rivalDelta;
      googleSum += r.googleDelta;
    }
    return {
      ...c,
      matrix,
      agree,
      conflict,
      rivalAvg: both.length ? rivalSum / both.length : null,
      googleAvg: both.length ? googleSum / both.length : null,
    };
  }, [allRows, both]);

  const rows = useMemo(() => {
    let r = allRows;
    if (source) r = r.filter((x) => x.source === source);
    if (cell) r = r.filter((x) => x.rivalSide === cell.r && x.googleSide === cell.g);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter((x) => (x.name || '').toLowerCase().includes(q) || (x.code || '').toLowerCase().includes(q));
    }
    const gap = (x) => (x.rivalDelta != null && x.googleDelta != null ? Math.abs(x.rivalDelta - x.googleDelta) : -1);
    const by = {
      disagreement: (a, b) => gap(b) - gap(a),
      google: (a, b) => (b.googleDelta ?? -1e9) - (a.googleDelta ?? -1e9),
      rivals: (a, b) => (b.rivalDelta ?? -1e9) - (a.rivalDelta ?? -1e9),
    }[sort];
    return [...r].sort(by);
  }, [allRows, source, cell, search, sort]);

  const pickCell = (r, g) => {
    if (cell && cell.r === r && cell.g === g) { setCell(null); return; }
    setCell({ r, g });
    setSource('both');
  };

  if (loading) {
    return (
      <div className="p-6 max-w-[1320px] mx-auto">
        <Card className="rounded-2xl border border-gray-100 shadow-sm p-10 flex items-center justify-center text-gray-500">
          <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Loading…
        </Card>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 max-w-[1320px] mx-auto">
        <Card className="rounded-2xl border border-red-100 shadow-sm p-8 text-red-600">
          Error: {error}
          <button onClick={fetchData} className="ml-3 underline">Try again</button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1320px] mx-auto">
      {/* Header */}
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white border-b">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
              <Scale className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Rival Prices vs Google Benchmark</h1>
              <p className="text-sm text-gray-500 mt-1 max-w-3xl">
                The same products side by side: the prices we collected from named sellers
                ({activeSites.join(', ') || 'none'}) and the single market price Google Merchant Center
                reports. Products are joined on the Merchant Center offer ID.
                {selectedBrand?.name ? ` · ${selectedBrand.name}` : ''}
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
              Our rival scan: <b className="tabular-nums">{data?.matchDate || '—'}</b>
            </span>
            {(data?.compSites || []).map((s) => {
              const stale = staleSites.includes(s);
              return (
                <span key={s} className={`px-3 py-1 rounded-full border ${stale
                  ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                  {s}: <span className="tabular-nums">{data?.siteDates?.[s] || '—'}</span>
                  {stale && ' · stale'}
                </span>
              );
            })}
            <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
              Google benchmark: <b className="tabular-nums">{data?.benchDate || '—'}</b> (daily)
            </span>
          </div>

          {staleSites.length > 0 && (
            <div className="mt-3 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                {staleSites.join(', ')} prices are more than {STALE_DAYS} days old, so
                {includeStale ? ' they are included below — read those columns with care.' : ' they are left out of the rival comparison.'}
                <label className="ml-2 inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={includeStale} onChange={(e) => setIncludeStale(e.target.checked)} />
                  Include anyway
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat label="In both sources" value={fmtNum(summary.both)} sub="compared below" />
          <Stat label="Only our rival scan" value={fmtNum(summary.rivals)} sub="Google gives no benchmark" />
          <Stat label="Only Google benchmark" value={fmtNum(summary.google)} sub="no seller match" />
          <Stat
            label="Sources agree"
            value={summary.both ? `${Math.round((summary.agree / summary.both) * 100)}%` : '—'}
            sub={`${fmtNum(summary.agree)} agree · ${fmtNum(summary.conflict)} opposite`}
          />
          <Stat
            label="Avg gap on the same products"
            value={`${fmtPct(summary.rivalAvg)} / ${fmtPct(summary.googleAvg)}`}
            sub="vs cheapest seller / vs Google"
          />
        </div>
      </Card>

      {/* Agreement matrix */}
      <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-700">
            Do the two sources agree?{' '}
            <span className="font-normal text-gray-400">
              ({fmtNum(summary.both)} products in both · click a cell to list those products)
            </span>
          </span>
        </div>
        <div className="p-5 overflow-auto">
          <table className="text-sm">
            <thead>
              <tr>
                <th className="text-left pr-4 pb-2 font-medium text-gray-400 text-xs">Our rival scan says ↓ · Google says →</th>
                {SIDES.map((g) => (
                  <th key={g} className={`px-4 pb-2 font-medium text-center ${SIDE_CLS[g]}`}>{SIDE_LABEL[g]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SIDES.map((r) => (
                <tr key={r}>
                  <td className={`pr-4 py-1 font-medium ${SIDE_CLS[r]}`}>{SIDE_LABEL[r]}</td>
                  {SIDES.map((g) => {
                    const n = summary.matrix[`${r}|${g}`] || 0;
                    const v = verdictOf(r, g);
                    const active = cell && cell.r === r && cell.g === g;
                    return (
                      <td key={g} className="px-1 py-1">
                        <button
                          type="button"
                          onClick={() => pickCell(r, g)}
                          disabled={!n}
                          className={`w-36 h-14 rounded-xl border text-lg font-semibold tabular-nums transition ${
                            active ? 'ring-2 ring-indigo-500' : ''} ${VERDICT[v].cls} ${n ? 'hover:brightness-95' : 'opacity-40'}`}
                        >
                          {fmtNum(n)}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-gray-400 max-w-3xl">
            Rival side compares our price on the scan day with the cheapest active seller; Google side
            compares our Merchant Center price with Google&apos;s benchmark. Diagonal = both sources
            say the same; red corners = one says cheaper, the other pricier.
          </p>
        </div>
      </Card>

      {/* Detail table */}
      <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden bg-white">
            {SOURCE_TABS.map((t, i) => (
              <button
                key={t.k || 'all'}
                type="button"
                onClick={() => { setSource(t.k); if (t.k !== 'both') setCell(null); }}
                className={`px-3 h-9 text-sm whitespace-nowrap ${i > 0 ? 'border-l border-gray-200' : ''} ${
                  source === t.k ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                {t.label}{' '}
                <span className="tabular-nums opacity-70">
                  {fmtNum(t.k ? summary[t.k] : summary.both + summary.rivals + summary.google)}
                </span>
              </button>
            ))}
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 h-9 text-sm bg-white"
          >
            <option value="disagreement">Sort: biggest disagreement</option>
            <option value="google">Sort: pricier vs Google first</option>
            <option value="rivals">Sort: pricier vs sellers first</option>
          </select>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search: product name, offer ID…"
              className="border border-gray-300 rounded-lg pl-9 pr-3 h-9 text-sm w-64 bg-white"
            />
          </div>
          {cell && (
            <button onClick={() => setCell(null)} className="text-sm text-indigo-600 underline">
              clear cell: {SIDE_LABEL[cell.r]} / {SIDE_LABEL[cell.g]}
            </button>
          )}
          <span className="text-sm text-gray-400 ml-auto tabular-nums">{fmtNum(rows.length)} rows</span>
        </div>
        <div className="overflow-auto max-h-[70vh]">
          <table className="min-w-full text-sm">
            <thead className="bg-white sticky top-0 z-10 border-b border-gray-100">
              <tr className="text-gray-500">
                <th className="text-left px-5 py-2.5 font-medium">Product</th>
                <th className="text-right px-3 py-2.5 font-medium">{data?.ourLabel || OUR_SITE}</th>
                {activeSites.map((s) => (
                  <th key={s} className="text-right px-3 py-2.5 font-medium">{s}</th>
                ))}
                <th className="text-right px-3 py-2.5 font-medium" title="Our price vs the cheapest active seller, on the scan day">vs sellers</th>
                <th className="text-right px-3 py-2.5 font-medium text-indigo-700 border-l border-gray-100">Google benchmark</th>
                <th className="text-right px-3 py-2.5 font-medium text-indigo-700" title="Our Merchant Center price vs Google's benchmark">vs Google</th>
                <th className="text-center px-5 py-2.5 font-medium">Verdict</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 500).map((r) => (
                <tr key={r.key} className="border-b border-gray-50 hover:bg-gray-50/60">
                  <td className="px-5 py-2">
                    <div className="flex items-center gap-3 min-w-[260px] max-w-[380px]">
                      {r.image
                        ? <img src={r.image} alt="" className="w-10 h-10 rounded-lg object-cover bg-gray-100 shrink-0" loading="lazy" />
                        : <div className="w-10 h-10 rounded-lg bg-gray-100 shrink-0" />}
                      <div className="min-w-0">
                        {r.url
                          ? <a href={r.url} target="_blank" rel="noreferrer" className="block truncate text-gray-900 hover:underline">{r.name || r.code}</a>
                          : <div className="truncate text-gray-900">{r.name || r.code}</div>}
                        <div className="text-xs text-gray-400">{r.code}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">
                    {r.url && r.ourPrice != null
                      ? <a href={r.url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline" title="Open on our site">{fmt(r.ourPrice)}</a>
                      : fmt(r.ourPrice)}
                    {r.mcPrice != null && r.scrapePrice != null && Math.abs(r.mcPrice - r.scrapePrice) > 1 && (
                      <div className="text-[11px] font-normal text-gray-400" title="Our price on the rival scan day">
                        scan day {fmt(r.scrapePrice)}
                      </div>
                    )}
                  </td>
                  {activeSites.map((s) => {
                    const c = r.comps?.[s];
                    const cheapest = c?.price != null && c.price === r.marketMin;
                    return (
                      <td key={s} className={`px-3 py-2 text-right tabular-nums ${cheapest ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                        {c?.price != null
                          ? (c.url ? <a href={c.url} target="_blank" rel="noreferrer" className="hover:underline">{fmt(c.price)}</a> : fmt(c.price))
                          : <span className="text-gray-300">—</span>}
                      </td>
                    );
                  })}
                  <td className={`px-3 py-2 text-right tabular-nums ${SIDE_CLS[r.rivalSide] || 'text-gray-300'}`}>
                    {fmtPct(r.rivalDelta)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums border-l border-gray-100">{fmt(r.benchmark)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${SIDE_CLS[r.googleSide] || 'text-gray-300'}`}>
                    {fmtPct(r.googleDelta)}
                  </td>
                  <td className="px-5 py-2 text-center">
                    {r.verdict
                      ? <span className={`inline-block px-2 py-0.5 rounded-full border text-xs ${VERDICT[r.verdict].cls}`}>{VERDICT[r.verdict].label}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6 + activeSites.length} className="px-5 py-8 text-center text-gray-400">No products.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {rows.length > 500 && (
          <div className="px-5 py-2 text-xs text-gray-400 border-t border-gray-100">
            Showing the first 500 of {fmtNum(rows.length)} rows — narrow with search or a matrix cell.
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-bold text-gray-900 tabular-nums mt-1">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}
